import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { leadsApi, teamApi, siteVisitsApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Phone, MessageCircle, Calendar, MapPin, Zap, User, Loader2, KanbanSquare, StickyNote, Home, ClipboardList, ArrowRight, Search, Plus, Paperclip, Video, Image as ImageIcon, X, Download } from 'lucide-react';
import { toast } from 'sonner';

// Solar Project Lifecycle - 16 buckets (from initial contact through installation to handover)
const STAGES = [
  { key: 'Tele Caller',            label: 'Tele Caller',           color: 'sky',     dot: 'bg-sky-500' },
  { key: 'Follow-ups',             label: 'Follow-ups',            color: 'cyan',    dot: 'bg-cyan-500' },
  { key: 'Admin (Qualify)',        label: 'Admin (Qualify)',       color: 'yellow',  dot: 'bg-yellow-500' },
  { key: 'Sales Manager',          label: 'Sales Manager',         color: 'indigo',  dot: 'bg-indigo-500' },
  { key: 'Design',                 label: 'Design',                color: 'violet',  dot: 'bg-violet-500' },
  { key: 'Proposal Shared',        label: 'Proposal Shared 📄',    color: 'purple',  dot: 'bg-purple-500' },
  { key: 'Post Proposal Follow-up',label: 'Post-Proposal F/U',     color: 'pink',    dot: 'bg-pink-500' },
  { key: 'Deal Won',               label: 'Deal Won 🏆',           color: 'emerald', dot: 'bg-emerald-500' },
  { key: 'Accounts',               label: 'Accounts',              color: 'amber',   dot: 'bg-amber-500' },
  { key: 'Installation Head - BOS',label: 'Installation Head (BOS)',color:'orange',  dot: 'bg-orange-500' },
  { key: 'Admin (Procurement)',    label: 'Admin (Procurement)',   color: 'rose',    dot: 'bg-rose-500' },
  { key: 'Sales Manager 80%',      label: 'Sales Manager 80%',     color: 'fuchsia', dot: 'bg-fuchsia-500' },
  { key: 'Installation',           label: 'Installation',          color: 'lime',    dot: 'bg-lime-500' },
  { key: 'BESCOM Team',            label: 'BESCOM Team',           color: 'teal',    dot: 'bg-teal-500' },
  { key: 'Handover & Complete',    label: 'Handover ✅',           color: 'green',   dot: 'bg-green-600' },
  { key: 'Lost / Dropped',         label: 'Lost / Dropped',        color: 'slate',   dot: 'bg-slate-400' },
];

const columnBg = {
  sky:     'bg-sky-50 border-sky-200',
  cyan:    'bg-cyan-50 border-cyan-200',
  yellow:  'bg-yellow-50 border-yellow-200',
  indigo:  'bg-indigo-50 border-indigo-200',
  violet:  'bg-violet-50 border-violet-200',
  purple:  'bg-purple-50 border-purple-200',
  pink:    'bg-pink-50 border-pink-200',
  emerald: 'bg-emerald-50 border-emerald-200',
  amber:   'bg-amber-50 border-amber-200',
  orange:  'bg-orange-50 border-orange-200',
  rose:    'bg-rose-50 border-rose-200',
  fuchsia: 'bg-fuchsia-50 border-fuchsia-200',
  lime:    'bg-lime-50 border-lime-200',
  teal:    'bg-teal-50 border-teal-200',
  green:   'bg-green-50 border-green-300',
  slate:   'bg-slate-50 border-slate-200',
};

const columnHeader = {
  sky: 'text-sky-800', cyan: 'text-cyan-800', yellow: 'text-yellow-800', indigo: 'text-indigo-800', violet: 'text-violet-800',
  purple: 'text-purple-800', pink: 'text-pink-800',
  emerald: 'text-emerald-800', amber: 'text-amber-800', orange: 'text-orange-800', rose: 'text-rose-800',
  fuchsia: 'text-fuchsia-800', lime: 'text-lime-800', teal: 'text-teal-800', green: 'text-green-800',
  slate: 'text-slate-700',
};

const ACTIVITY_ICON = {
  call: Phone,
  whatsapp: MessageCircle,
  note: StickyNote,
  site_visit: Home,
  stage_change: ArrowRight,
  handoff: User,
  proposal_sent: ClipboardList,
  email: StickyNote,
};

const ACTIVITY_LABEL = {
  call: 'Call logged',
  whatsapp: 'WhatsApp',
  note: 'Note',
  site_visit: 'Site visit',
  stage_change: 'Stage change',
  handoff: 'Owner handoff',
  proposal_sent: 'Proposal sent',
  email: 'Email',
};

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diff = Math.round((d - now) / 86400000);
  if (Math.abs(diff) < 1) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  if (diff > 1 && diff < 8) return `in ${diff} days`;
  if (diff < -1 && diff > -8) return `${Math.abs(diff)}d ago`;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

const fmtDateTime = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

export default function Pipeline() {
  const { user } = useAuth();
  const isAdmin = user && (user.role === 'Director' || user.role === 'Admin');
  const [leads, setLeads] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragId, setDragId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [activities, setActivities] = useState([]);
  const [actLoading, setActLoading] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteKind, setNoteKind] = useState('note');
  const [savingAct, setSavingAct] = useState(false);
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [mineOnly, setMineOnly] = useState(false); // Sales reps can toggle to only see their assigned leads
  const [q, setQ] = useState('');
  // Site visit dialog state
  const [visitOpen, setVisitOpen] = useState(false);
  const [visitForm, setVisitForm] = useState({ scheduled_at: '', engineer_id: '', address: '', notes: '' });
  const [visits, setVisits] = useState([]);
  const [savingVisit, setSavingVisit] = useState(false);
  // Attachments + lat/lng
  const [attachments, setAttachments] = useState([]);
  const [uploadingKind, setUploadingKind] = useState(null); // ebill | site_photo | site_video
  const [latLng, setLatLng] = useState({ lat: '', lng: '' });
  const [savingLatLng, setSavingLatLng] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [ls, ts] = await Promise.all([leadsApi.list(), teamApi.list().catch(() => [])]);
      setLeads(ls);
      setTeam(ts);
    } catch (e) {
      toast.error('Could not load pipeline');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const teamMap = useMemo(() => {
    const m = {};
    team.forEach(t => { m[t.id] = t; });
    return m;
  }, [team]);

  const filteredLeads = useMemo(() => {
    let out = leads;
    // Non-admin: default = see the whole pipeline (baton-pass rule still enforces who can MOVE cards).
    // "Mine only" toggle lets a rep narrow down to just their owned leads.
    if (!isAdmin && mineOnly) out = out.filter(l => l.assigned_to === user.id);
    else if (isAdmin && ownerFilter !== 'all') out = out.filter(l => l.assigned_to === ownerFilter);
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      out = out.filter(l =>
        (l.name || '').toLowerCase().includes(s) ||
        (l.phone || '').toLowerCase().includes(s) ||
        (l.city || '').toLowerCase().includes(s) ||
        (l.id || '').toLowerCase().includes(s)
      );
    }
    return out;
  }, [leads, isAdmin, user, ownerFilter, mineOnly, q]);

  const grouped = useMemo(() => {
    const g = Object.fromEntries(STAGES.map(s => [s.key, []]));
    filteredLeads.forEach(l => {
      const st = STAGES.find(s => s.key === l.status) ? l.status : 'New';
      g[st].push(l);
    });
    return g;
  }, [filteredLeads]);

  const moveTo = async (leadId, nextStatus) => {
    const current = leads.find(l => l.id === leadId);
    if (!current || current.status === nextStatus) return;
    // Permission check (same as backend)
    const canMove = isAdmin || (current.assigned_to && current.assigned_to === user.id);
    if (!canMove) {
      toast.error(current.assigned_to
        ? 'Only the lead owner or an Admin/Director can change the stage.'
        : 'Unassigned lead — ask an Admin to assign an owner first.');
      return;
    }
    // Optimistic update
    setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status: nextStatus } : l));
    try {
      await leadsApi.update(leadId, { status: nextStatus });
      toast.success(`${current.name} → ${nextStatus}`);
      // If drawer is open for this lead, refresh activities to show new stage_change
      if (selected?.id === leadId) {
        setSelected(prev => ({ ...prev, status: nextStatus }));
        loadActivities(leadId);
      }
    } catch (e) {
      // Rollback
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status: current.status } : l));
      toast.error(e?.response?.data?.detail || 'Could not move lead');
    }
  };

  const changeOwner = async (leadId, newOwnerId) => {
    const current = leads.find(l => l.id === leadId);
    if (!current) return;
    const assigned_to = newOwnerId === '__unassigned__' ? null : newOwnerId;
    setLeads(prev => prev.map(l => l.id === leadId ? { ...l, assigned_to } : l));
    try {
      await leadsApi.update(leadId, { assigned_to });
      const owner = team.find(t => t.id === assigned_to);
      toast.success(`${current.name} assigned to ${owner ? owner.name : 'no one'}`);
      if (selected?.id === leadId) setSelected(prev => ({ ...prev, assigned_to }));
    } catch (e) {
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, assigned_to: current.assigned_to } : l));
      toast.error('Could not change owner');
    }
  };

  const loadActivities = async (id) => {
    setActLoading(true);
    try {
      const a = await leadsApi.activities(id);
      setActivities(a);
    } catch (e) {
      setActivities([]);
    } finally { setActLoading(false); }
  };

  const openLead = async (lead) => {
    setSelected(lead);
    setNoteText('');
    setNoteKind('note');
    setVisitOpen(false);
    setVisitForm({ scheduled_at: '', engineer_id: '', address: lead.city || '', notes: '' });
    setLatLng({ lat: lead.lat != null ? String(lead.lat) : '', lng: lead.lng != null ? String(lead.lng) : '' });
    await Promise.all([
      loadActivities(lead.id),
      siteVisitsApi.list({ lead_id: lead.id }).then(setVisits).catch(() => setVisits([])),
      leadsApi.attachments(lead.id).then(setAttachments).catch(() => setAttachments([])),
    ]);
  };

  const saveLatLng = async () => {
    if (!selected) return;
    const lat = latLng.lat === '' ? null : Number(latLng.lat);
    const lng = latLng.lng === '' ? null : Number(latLng.lng);
    if ((lat != null && isNaN(lat)) || (lng != null && isNaN(lng))) { toast.error('Enter valid numeric lat/lng'); return; }
    setSavingLatLng(true);
    try {
      const updated = await leadsApi.update(selected.id, { lat, lng });
      setLeads(prev => prev.map(l => l.id === updated.id ? updated : l));
      setSelected(updated);
      // Log activity for traceability
      await leadsApi.addActivity(selected.id, {
        kind: 'note',
        text: `Location updated: ${lat}, ${lng}`,
      });
      loadActivities(selected.id);
      toast.success('Lat/Lng saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save lat/lng');
    } finally { setSavingLatLng(false); }
  };

  const useGPS = () => {
    if (!navigator.geolocation) { toast.error('GPS not available on this device'); return; }
    toast.info('Requesting GPS…');
    navigator.geolocation.getCurrentPosition(
      (pos) => setLatLng({ lat: pos.coords.latitude.toFixed(6), lng: pos.coords.longitude.toFixed(6) }),
      (err) => toast.error(err.message || 'Could not get location'),
      { enableHighAccuracy: true, timeout: 12000 }
    );
  };

  const uploadAttachment = async (kind, file) => {
    if (!selected || !file) return;
    setUploadingKind(kind);
    try {
      const created = await leadsApi.uploadAttachment(selected.id, kind, file);
      setAttachments(prev => [created, ...prev]);
      await loadActivities(selected.id);
      toast.success(`${file.name} uploaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally { setUploadingKind(null); }
  };

  const removeAttachment = async (att) => {
    if (!selected) return;
    if (!window.confirm(`Remove ${att.original_filename}?`)) return;
    try {
      await leadsApi.deleteAttachment(selected.id, att.id);
      setAttachments(prev => prev.filter(a => a.id !== att.id));
      toast.success('Attachment removed');
    } catch (e) { toast.error('Delete failed'); }
  };

  const scheduleVisit = async () => {
    if (!selected || !visitForm.scheduled_at) { toast.error('Please pick a date & time'); return; }
    setSavingVisit(true);
    try {
      const created = await siteVisitsApi.create({
        lead_id: selected.id,
        scheduled_at: new Date(visitForm.scheduled_at).toISOString(),
        engineer_id: visitForm.engineer_id || null,
        address: visitForm.address || null,
        notes: visitForm.notes || null,
      });
      setVisits(prev => [created, ...prev]);
      // refresh activities to see the auto-log
      await loadActivities(selected.id);
      toast.success('Site visit scheduled');
      setVisitOpen(false);
      setVisitForm({ scheduled_at: '', engineer_id: '', address: '', notes: '' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not schedule visit');
    } finally { setSavingVisit(false); }
  };

  const markVisitDone = async (id) => {
    try {
      const updated = await siteVisitsApi.update(id, { status: 'Completed' });
      setVisits(prev => prev.map(v => v.id === id ? updated : v));
      if (selected) await loadActivities(selected.id);
      toast.success('Visit marked completed');
    } catch (e) { toast.error('Could not update visit'); }
  };

  const addActivity = async (kind, text, meta) => {
    if (!selected) return;
    try {
      const created = await leadsApi.addActivity(selected.id, { kind, text, meta });
      setActivities(prev => [created, ...prev]);
      return created;
    } catch (e) {
      toast.error('Could not log activity');
    }
  };

  const submitNote = async () => {
    if (!noteText.trim()) { toast.error('Please write something'); return; }
    setSavingAct(true);
    const created = await addActivity(noteKind, noteText.trim());
    if (created) {
      setNoteText('');
      toast.success('Activity logged');
    }
    setSavingAct(false);
  };

  const setFollowUp = async (isoDate) => {
    if (!selected) return;
    try {
      const updated = await leadsApi.update(selected.id, { follow_up_date: isoDate });
      setLeads(prev => prev.map(l => l.id === updated.id ? updated : l));
      setSelected(updated);
      await addActivity('note', `Follow-up scheduled for ${fmtDateTime(isoDate)}`);
      toast.success('Follow-up saved');
    } catch (e) { toast.error('Could not save follow-up'); }
  };

  const callAndLog = async (lead) => {
    // Trigger phone dialer
    window.location.href = `tel:${lead.phone}`;
    // Auto-log after 1s to give user time to see it triggered
    setTimeout(async () => {
      try {
        await leadsApi.addActivity(lead.id, { kind: 'call', text: `Call initiated to ${lead.phone}` });
        if (selected?.id === lead.id) loadActivities(lead.id);
      } catch (_e) { /* non-blocking */ }
    }, 500);
  };

  const whatsappAndLog = async (lead) => {
    const phone = (lead.phone || '').replace(/\D/g, '');
    const msg = encodeURIComponent(`Hi ${lead.name}, this is ${user.name} from Inpace Power. Following up on your solar proposal.`);
    window.open(`https://wa.me/${phone.startsWith('91') ? phone : '91' + phone}?text=${msg}`, '_blank');
    try {
      await leadsApi.addActivity(lead.id, { kind: 'whatsapp', text: `WhatsApp message initiated to ${lead.phone}` });
      if (selected?.id === lead.id) loadActivities(lead.id);
    } catch (_e) { /* non-blocking */ }
  };

  // Follow-up chip color
  const followChip = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    const diff = Math.round((d - new Date()) / 86400000);
    let color = 'bg-slate-100 text-slate-700';
    if (diff < 0) color = 'bg-rose-100 text-rose-800';
    else if (diff === 0) color = 'bg-orange-100 text-orange-800';
    else if (diff <= 2) color = 'bg-amber-100 text-amber-800';
    else color = 'bg-emerald-50 text-emerald-800';
    return <Badge className={`${color} hover:bg-inherit border-0 text-[10px] rounded-full`}><Calendar className="w-3 h-3 mr-1" />{fmtDate(iso)}</Badge>;
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-emerald-950 flex items-center gap-2">
            <KanbanSquare className="w-7 h-7 text-orange-600" /> Sales Pipeline
          </h1>
          <p className="text-slate-600 mt-1 text-sm">Drag any lead across stages · click a card to log activity or set a follow-up · {filteredLeads.length} lead{filteredLeads.length !== 1 ? 's' : ''} visible</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input placeholder="Search name / phone / city…" value={q} onChange={e => setQ(e.target.value)} className="pl-9 w-64" data-testid="pipeline-search" />
          </div>
          {isAdmin && (
            <Select value={ownerFilter} onValueChange={setOwnerFilter}>
              <SelectTrigger className="w-48" data-testid="pipeline-owner-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All owners</SelectItem>
                {team.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {!isAdmin && (
            <label className="inline-flex items-center gap-2 h-10 px-3 rounded-md border border-emerald-100 bg-white cursor-pointer" data-testid="pipeline-mine-only-label">
              <input
                type="checkbox"
                checked={mineOnly}
                onChange={e => setMineOnly(e.target.checked)}
                className="w-4 h-4 accent-emerald-700"
                data-testid="pipeline-mine-only"
              />
              <span className="text-sm text-emerald-900">Mine only</span>
            </label>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-slate-500"><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading pipeline…</div>
      ) : (
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-3 min-w-max">
            {STAGES.map(stage => {
              const cards = grouped[stage.key] || [];
              const totalKw = cards.reduce((s, l) => s + (l.kw || 0), 0);
              return (
                <div
                  key={stage.key}
                  className={`w-72 shrink-0 rounded-xl border-2 border-dashed ${columnBg[stage.color]} transition-colors`}
                  onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('ring-2', 'ring-orange-400'); }}
                  onDragLeave={(e) => { e.currentTarget.classList.remove('ring-2', 'ring-orange-400'); }}
                  onDrop={(e) => {
                    e.currentTarget.classList.remove('ring-2', 'ring-orange-400');
                    if (dragId) moveTo(dragId, stage.key);
                    setDragId(null);
                  }}
                  data-testid={`pipeline-col-${stage.key.replace(/ /g,'-')}`}
                >
                  <div className={`px-3 py-2.5 flex items-center justify-between border-b border-white/60`}>
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${stage.dot}`}></span>
                      <span className={`text-sm font-extrabold uppercase tracking-wide ${columnHeader[stage.color]}`}>{stage.label}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Badge className="bg-white/80 text-slate-700 border-0 text-[10px] font-bold">{cards.length}</Badge>
                      {totalKw > 0 && <Badge className="bg-white/80 text-orange-700 border-0 text-[10px] font-bold">{totalKw.toFixed(1)} kW</Badge>}
                    </div>
                  </div>
                  <div className="p-2 space-y-2 min-h-[200px] max-h-[calc(100vh-260px)] overflow-y-auto">
                    {cards.length === 0 && (
                      <div className="text-xs text-slate-400 py-8 text-center italic">Drop leads here</div>
                    )}
                    {cards.map(l => {
                      const owner = teamMap[l.assigned_to];
                      return (
                        <Card
                          key={l.id}
                          draggable
                          onDragStart={(e) => { setDragId(l.id); e.dataTransfer.effectAllowed = 'move'; }}
                          onDragEnd={() => setDragId(null)}
                          className={`p-3 cursor-grab active:cursor-grabbing bg-white hover:shadow-lg transition-all border border-slate-200 ${dragId === l.id ? 'opacity-40' : ''}`}
                          data-testid={`pipeline-card-${l.id}`}
                        >
                          <div className="flex items-start justify-between gap-2 cursor-pointer" onClick={() => openLead(l)}>
                            <div className="min-w-0 flex-1">
                              <div className="font-bold text-emerald-950 text-sm truncate">{l.name}</div>
                              <div className="text-[11px] text-slate-500 truncate">{l.id} · {l.segment} · {l.phone}</div>
                            </div>
                            {owner && (
                              <div title={owner.name} className="w-7 h-7 shrink-0 rounded-full bg-gradient-to-br from-emerald-600 to-emerald-800 text-white text-[10px] font-bold flex items-center justify-center">
                                {owner.avatar || owner.name?.[0]}
                              </div>
                            )}
                          </div>
                          <div className="mt-2 flex items-center gap-1.5 flex-wrap cursor-pointer" onClick={() => openLead(l)}>
                            <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100 border-0 text-[10px]"><Zap className="w-3 h-3 mr-1" />{l.kw} kW</Badge>
                            <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 border-0 text-[10px]"><MapPin className="w-3 h-3 mr-1" />{l.city}</Badge>
                            {followChip(l.follow_up_date)}
                          </div>

                          {/* Move to & Change owner dropdowns (permission-gated) */}
                          <div className="mt-2 grid grid-cols-2 gap-1.5" onClick={(e) => e.stopPropagation()}>
                            {(() => {
                              const canMove = isAdmin || (l.assigned_to && l.assigned_to === user.id);
                              return (
                                <Select value={l.status} onValueChange={(v) => canMove && moveTo(l.id, v)} disabled={!canMove}>
                                  <SelectTrigger
                                    className={`h-7 text-[10px] px-2 border-slate-200 ${!canMove ? 'opacity-60 cursor-not-allowed' : ''}`}
                                    title={canMove ? '' : (l.assigned_to ? 'Only the owner or an Admin can change the stage' : 'Unassigned lead — ask Admin to assign an owner first')}
                                    data-testid={`move-${l.id}`}
                                  >
                                    <SelectValue placeholder="Move to…" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {STAGES.map(s => (
                                      <SelectItem key={s.key} value={s.key} className="text-xs">
                                        <span className="flex items-center gap-2"><span className={`w-1.5 h-1.5 rounded-full ${s.dot}`}></span>{s.label}</span>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              );
                            })()}
                            {(() => {
                              const canReassign = isAdmin || (l.assigned_to && l.assigned_to === user.id);
                              return canReassign ? (
                                <Select value={l.assigned_to || '__unassigned__'} onValueChange={(v) => changeOwner(l.id, v)}>
                                  <SelectTrigger className="h-7 text-[10px] px-2 border-slate-200" data-testid={`owner-${l.id}`}>
                                    <SelectValue placeholder="Owner…" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {isAdmin && <SelectItem value="__unassigned__" className="text-xs">— Unassigned —</SelectItem>}
                                    {team.map(t => (
                                      <SelectItem key={t.id} value={t.id} className="text-xs">{t.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <div className="h-7 text-[10px] px-2 border border-slate-200 rounded-md flex items-center gap-1.5 bg-slate-50 text-slate-600" title="Only the current owner or Admin can hand off">
                                  <User className="w-3 h-3" /> <span className="truncate">{teamMap[l.assigned_to]?.name || 'Unassigned'}</span>
                                </div>
                              );
                            })()}
                          </div>

                          <div className="mt-2 flex items-center gap-1 pt-2 border-t border-slate-100">
                            <button
                              onClick={(e) => { e.stopPropagation(); callAndLog(l); }}
                              className="flex-1 flex items-center justify-center gap-1 text-[11px] text-slate-600 hover:text-orange-700 hover:bg-orange-50 rounded py-1 transition-colors"
                              data-testid={`pipeline-call-${l.id}`}>
                              <Phone className="w-3.5 h-3.5" /> Call
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); whatsappAndLog(l); }}
                              className="flex-1 flex items-center justify-center gap-1 text-[11px] text-slate-600 hover:text-emerald-700 hover:bg-emerald-50 rounded py-1 transition-colors"
                              data-testid={`pipeline-wa-${l.id}`}>
                              <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                            </button>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Lead detail drawer */}
      <Sheet open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="text-emerald-950 flex items-center gap-2">
                  <User className="w-5 h-5 text-orange-600" /> {selected.name}
                </SheetTitle>
                <div className="text-xs text-slate-500">{selected.id} · {selected.segment} · {selected.city}</div>
              </SheetHeader>

              <div className="mt-4 grid grid-cols-2 gap-2">
                <div className="p-2.5 bg-orange-50 rounded-lg">
                  <div className="text-[10px] text-orange-800 font-bold uppercase">System size</div>
                  <div className="text-sm font-bold text-emerald-950">{selected.kw} kW</div>
                </div>
                <div className="p-2.5 bg-slate-50 rounded-lg">
                  <div className="text-[10px] text-slate-600 font-bold uppercase">Owner</div>
                  <div className="text-sm font-bold text-emerald-950">{teamMap[selected.assigned_to]?.name || 'Unassigned'}</div>
                </div>
                <div className="p-2.5 bg-slate-50 rounded-lg">
                  <div className="text-[10px] text-slate-600 font-bold uppercase">Phone</div>
                  <div className="text-sm font-bold text-emerald-950">{selected.phone}</div>
                </div>
                <div className="p-2.5 bg-slate-50 rounded-lg">
                  <div className="text-[10px] text-slate-600 font-bold uppercase">Stage</div>
                  <div className="text-sm font-bold text-emerald-950">{selected.status}</div>
                </div>
              </div>

              {/* Quick actions */}
              <div className="mt-4 flex gap-2">
                <Button size="sm" variant="outline" onClick={() => callAndLog(selected)} className="flex-1"><Phone className="w-4 h-4 mr-1" /> Call</Button>
                <Button size="sm" variant="outline" onClick={() => whatsappAndLog(selected)} className="flex-1 border-emerald-200 text-emerald-700"><MessageCircle className="w-4 h-4 mr-1" /> WhatsApp</Button>
              </div>

              {/* Follow-up date */}
              <div className="mt-5">
                <Label className="text-xs font-bold text-slate-600 uppercase">Next Follow-up</Label>
                <div className="mt-1.5 flex gap-2 items-center">
                  <Input
                    type="datetime-local"
                    value={selected.follow_up_date ? new Date(selected.follow_up_date).toISOString().slice(0, 16) : ''}
                    onChange={(e) => e.target.value && setFollowUp(new Date(e.target.value).toISOString())}
                    className="text-xs"
                    data-testid="pipeline-followup-input"
                  />
                  {selected.follow_up_date && followChip(selected.follow_up_date)}
                </div>
              </div>

              {/* Rooftop Lat/Lng */}
              <div className="mt-5">
                <Label className="text-xs font-bold text-slate-600 uppercase">Rooftop Location (Lat / Lng)</Label>
                <div className="mt-1.5 grid grid-cols-2 gap-2">
                  <Input placeholder="Latitude e.g. 12.9716" value={latLng.lat} onChange={e => setLatLng({ ...latLng, lat: e.target.value })} className="text-xs" data-testid="lat-input" />
                  <Input placeholder="Longitude e.g. 77.5946" value={latLng.lng} onChange={e => setLatLng({ ...latLng, lng: e.target.value })} className="text-xs" data-testid="lng-input" />
                </div>
                <div className="mt-2 flex gap-2">
                  <Button size="sm" variant="outline" onClick={useGPS} className="text-[11px] flex-1" data-testid="gps-btn"><MapPin className="w-3 h-3 mr-1" /> Use My GPS</Button>
                  <Button size="sm" onClick={saveLatLng} disabled={savingLatLng} className="bg-orange-600 hover:bg-orange-700 text-white text-[11px] flex-1" data-testid="save-latlng">
                    {savingLatLng && <Loader2 className="w-3 h-3 mr-1 animate-spin" />} Save
                  </Button>
                </div>
                {selected.lat != null && selected.lng != null && (
                  <a href={`https://www.google.com/maps?q=${selected.lat},${selected.lng}`} target="_blank" rel="noopener noreferrer" className="mt-1.5 inline-block text-[11px] text-orange-700 hover:underline">Open in Google Maps ↗</a>
                )}
              </div>

              {/* Attachments — E-bills / Site photos / Site videos */}
              <div className="mt-5">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-bold text-slate-600 uppercase">Attachments ({attachments.length})</Label>
                </div>
                <div className="grid grid-cols-3 gap-1.5">
                  {[
                    { kind: 'ebill', label: 'E-Bill', accept: 'image/*,application/pdf', icon: Paperclip, hint: 'JPG / PDF' },
                    { kind: 'site_photo', label: 'Site Photo', accept: 'image/*', icon: ImageIcon, hint: 'JPG / PNG' },
                    { kind: 'site_video', label: 'Site Video', accept: 'video/*', icon: Video, hint: 'MP4 · max 60MB' },
                  ].map(({ kind, label, accept, icon: I, hint }) => (
                    <label key={kind} className={`p-2 rounded-lg border border-dashed border-slate-300 hover:border-orange-400 hover:bg-orange-50 cursor-pointer flex flex-col items-center text-center transition-colors ${uploadingKind === kind ? 'opacity-50 pointer-events-none' : ''}`}>
                      {uploadingKind === kind ? <Loader2 className="w-4 h-4 text-orange-600 animate-spin" /> : <I className="w-4 h-4 text-orange-600" />}
                      <div className="text-[10px] font-bold mt-1 text-slate-700">{label}</div>
                      <div className="text-[9px] text-slate-400">{hint}</div>
                      <input type="file" accept={accept} className="hidden" data-testid={`upload-${kind}`}
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadAttachment(kind, f); e.target.value = ''; }} />
                    </label>
                  ))}
                </div>
                {attachments.length > 0 && (
                  <div className="mt-2 space-y-1.5">
                    {attachments.map(a => {
                      const isImage = a.content_type?.startsWith('image/');
                      const isVideo = a.content_type?.startsWith('video/');
                      const url = leadsApi.attachmentUrl(selected.id, a.id);
                      const kindTag = {ebill:'E-Bill', site_photo:'Photo', site_video:'Video'}[a.kind] || a.kind;
                      const kindColor = {ebill:'bg-purple-100 text-purple-800', site_photo:'bg-sky-100 text-sky-800', site_video:'bg-pink-100 text-pink-800'}[a.kind] || 'bg-slate-100';
                      return (
                        <div key={a.id} className="flex items-center gap-2 p-1.5 bg-slate-50 rounded-lg">
                          {isImage ? (
                            <a href={url} target="_blank" rel="noopener noreferrer" className="shrink-0">
                              <img src={url} alt={a.original_filename} className="w-10 h-10 rounded object-cover border border-slate-200" />
                            </a>
                          ) : isVideo ? (
                            <a href={url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded bg-pink-100 flex items-center justify-center shrink-0"><Video className="w-4 h-4 text-pink-700" /></a>
                          ) : (
                            <a href={url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded bg-purple-100 flex items-center justify-center shrink-0"><Paperclip className="w-4 h-4 text-purple-700" /></a>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-[11px] font-semibold text-emerald-950 truncate">{a.original_filename}</div>
                            <div className="text-[9px] text-slate-500 flex items-center gap-1 mt-0.5">
                              <Badge className={`${kindColor} border-0 text-[8px] px-1 py-0`}>{kindTag}</Badge>
                              <span>{(a.size/1024).toFixed(0)} KB · {a.uploaded_by_name}</span>
                            </div>
                          </div>
                          <a href={url} target="_blank" rel="noopener noreferrer" className="p-1 hover:bg-white rounded" title="Open"><Download className="w-3.5 h-3.5 text-slate-500" /></a>
                          <button onClick={() => removeAttachment(a)} className="p-1 hover:bg-rose-50 rounded" title="Remove"><X className="w-3.5 h-3.5 text-rose-500" /></button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Site Visits */}
              <div className="mt-5">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-bold text-slate-600 uppercase">Site Visits ({visits.length})</Label>
                  <Button size="sm" variant="outline" className="h-7 text-[11px] border-teal-200 text-teal-700" onClick={() => setVisitOpen(v => !v)} data-testid="toggle-schedule-visit">
                    <Plus className="w-3 h-3 mr-1" /> {visitOpen ? 'Cancel' : 'Schedule'}
                  </Button>
                </div>
                {visitOpen && (
                  <div className="p-3 bg-teal-50 border border-teal-100 rounded-lg space-y-2 mb-2">
                    <Input type="datetime-local" value={visitForm.scheduled_at} onChange={e => setVisitForm({ ...visitForm, scheduled_at: e.target.value })} className="text-xs" data-testid="visit-datetime" />
                    <Select value={visitForm.engineer_id || 'none'} onValueChange={v => setVisitForm({ ...visitForm, engineer_id: v === 'none' ? '' : v })}>
                      <SelectTrigger className="text-xs" data-testid="visit-engineer"><SelectValue placeholder="Assign engineer" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— No engineer —</SelectItem>
                        {team.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input placeholder="Address on-site" value={visitForm.address} onChange={e => setVisitForm({ ...visitForm, address: e.target.value })} className="text-xs" />
                    <Textarea placeholder="Notes (bring drone, meet at gate…)" rows={2} value={visitForm.notes} onChange={e => setVisitForm({ ...visitForm, notes: e.target.value })} className="text-xs" />
                    <Button size="sm" onClick={scheduleVisit} disabled={savingVisit} className="bg-teal-600 hover:bg-teal-700 text-white w-full" data-testid="submit-visit">
                      {savingVisit && <Loader2 className="w-3 h-3 mr-1 animate-spin" />} Confirm Visit
                    </Button>
                  </div>
                )}
                {visits.length === 0 ? (
                  <div className="text-[11px] text-slate-400 italic py-1">No visits scheduled.</div>
                ) : (
                  <div className="space-y-1.5">
                    {visits.map(v => (
                      <div key={v.id} className={`p-2 rounded-lg flex items-center justify-between gap-2 ${v.status === 'Completed' ? 'bg-emerald-50 border border-emerald-100' : v.status === 'Cancelled' ? 'bg-slate-50 border border-slate-100 opacity-70' : 'bg-teal-50 border border-teal-100'}`}>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold text-emerald-950">{fmtDateTime(v.scheduled_at)}</div>
                          <div className="text-[10px] text-slate-600">{v.engineer_name || 'No engineer'} · {v.status}</div>
                          {v.notes && <div className="text-[10px] text-slate-500 mt-0.5 truncate">{v.notes}</div>}
                        </div>
                        {v.status === 'Scheduled' && (
                          <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2 text-emerald-700" onClick={() => markVisitDone(v.id)} data-testid={`complete-visit-${v.id}`}>Mark done</Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Add activity */}
              <div className="mt-5">
                <Label className="text-xs font-bold text-slate-600 uppercase">Log Activity</Label>
                <div className="mt-1.5 flex gap-2 mb-2">
                  <Select value={noteKind} onValueChange={setNoteKind}>
                    <SelectTrigger className="w-36 text-xs" data-testid="activity-kind"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="note">Note</SelectItem>
                      <SelectItem value="call">Call</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp</SelectItem>
                      <SelectItem value="site_visit">Site visit</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Textarea
                  placeholder="e.g. Called, customer wants site visit next Monday…"
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  rows={2}
                  className="text-sm"
                  data-testid="activity-text"
                />
                <Button size="sm" onClick={submitNote} disabled={savingAct} className="mt-2 bg-orange-600 hover:bg-orange-700 text-white" data-testid="submit-activity">
                  {savingAct && <Loader2 className="w-3 h-3 mr-1 animate-spin" />} Log Activity
                </Button>
              </div>

              {/* Timeline */}
              <div className="mt-6">
                <Label className="text-xs font-bold text-slate-600 uppercase">Activity Timeline</Label>
                {actLoading ? (
                  <div className="py-6 text-center text-slate-400 text-sm"><Loader2 className="w-4 h-4 inline animate-spin mr-1" /> Loading…</div>
                ) : activities.length === 0 ? (
                  <div className="py-6 text-center text-slate-400 text-sm italic">No activities yet. Log one above.</div>
                ) : (
                  <div className="mt-2 space-y-2">
                    {activities.map(a => {
                      const Icon = ACTIVITY_ICON[a.kind] || StickyNote;
                      return (
                        <div key={a.id} className="flex gap-2.5 p-2.5 bg-slate-50 rounded-lg">
                          <div className="w-7 h-7 rounded-full bg-white shadow flex items-center justify-center shrink-0">
                            <Icon className="w-3.5 h-3.5 text-orange-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-[11px] font-bold text-orange-700 uppercase tracking-wide">{ACTIVITY_LABEL[a.kind] || a.kind}</span>
                              <span className="text-[10px] text-slate-500">{fmtDateTime(a.created_at)}</span>
                            </div>
                            <div className="text-xs text-slate-700 mt-0.5">{a.text || '—'}</div>
                            <div className="text-[10px] text-slate-500 mt-0.5">by {a.created_by_name || '—'}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
